import { useCallback, useEffect, useState } from "react";
import {
  clearWaCredentials,
  fetchWaStatus,
  regenVerifyToken,
  saveWaCredentials,
  sendTemplate,
  subscribeWaApp,
  type WaStatus,
} from "@/services/waAgent";
import { btnDanger, btnGhost, btnPrimary, cardCls, inputCls } from "./ui";

const QUALITY: Record<string, { label: string; cls: string }> = {
  GREEN: { label: "Alta", cls: "bg-primary-500/15 text-primary-700" },
  YELLOW: { label: "Media", cls: "bg-amber-500/15 text-amber-700" },
  RED: { label: "Baja", cls: "bg-accent-500/15 text-accent-700" },
  UNKNOWN: { label: "Sin datos", cls: "bg-background-200 text-foreground-600" },
};

function Step({ n, title, done, children }: { n: number; title: string; done?: boolean; children: React.ReactNode }) {
  return (
    <div className={cardCls}>
      <div className="flex items-center gap-3 mb-3">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-primary-500 text-background-950" : "bg-background-200 text-foreground-700"}`}>
          {done ? <i className="ri-check-line"></i> : n}
        </span>
        <h3 className="font-heading font-bold text-foreground-950">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function CopyField({ label, value, notify }: { label: string; value: string; notify: (m: string) => void }) {
  return (
    <label className="block">
      <span className="text-xs text-foreground-500">{label}</span>
      <div className="flex gap-2 mt-1">
        <input readOnly value={value} className={`${inputCls} font-mono text-xs`} onFocus={(e) => e.currentTarget.select()} />
        <button
          className={btnGhost}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              notify("Copiado");
            } catch {
              notify("Selecciónalo y cópialo a mano");
            }
          }}
        >
          Copiar
        </button>
      </div>
    </label>
  );
}

export default function ConnectionTab({ notify, goTemplates }: { notify: (m: string) => void; goTemplates: () => void }) {
  const [st, setSt] = useState<WaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState("");
  const [phoneId, setPhoneId] = useState("");
  const [waba, setWaba] = useState("");
  const [secret, setSecret] = useState("");
  const [testTo, setTestTo] = useState("");
  const [testName, setTestName] = useState("vivelo_webinar_bienvenida");

  const load = useCallback(async () => {
    setLoading(true);
    const r = (await fetchWaStatus()) as Partial<WaStatus> & { error?: string };
    if (!("hasToken" in r)) setErr(r.error ?? "No se pudo cargar el estado");
    else {
      setErr(null);
      setSt(r as WaStatus);
      setPhoneId(r.phoneNumberId ?? "");
      setWaba(r.wabaId ?? "");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !st) return <div className="text-sm text-foreground-500">Cargando…</div>;
  if (err) return <div className={`${cardCls} text-sm text-accent-700`}>{err}</div>;
  if (!st) return null;

  const connected = st.connection === "ok";
  const q = QUALITY[st.quality ?? "UNKNOWN"] ?? QUALITY.UNKNOWN;

  const save = async () => {
    setBusy(true);
    const r = await saveWaCredentials({
      accessToken: token || undefined,
      phoneNumberId: phoneId !== (st.phoneNumberId ?? "") ? phoneId : undefined,
      wabaId: waba !== (st.wabaId ?? "") ? waba : undefined,
      appSecret: secret || undefined,
    });
    setBusy(false);
    if (r.ok) {
      setToken("");
      setSecret("");
      notify("Guardado");
      await load();
    } else notify(r.error ?? "No se pudo guardar");
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div className={`${cardCls} flex flex-wrap items-center justify-between gap-3`}>
        <div className="flex items-center gap-3">
          <span className={`w-3 h-3 rounded-full ${connected ? "bg-primary-500" : st.connection === "error" ? "bg-accent-500" : "bg-background-400"}`}></span>
          <div>
            <div className="font-heading font-bold text-foreground-950">
              {connected ? `Conectado${st.displayPhone ? ` · ${st.displayPhone}` : ""}` : st.connection === "error" ? "Error de conexión" : "Sin conectar"}
            </div>
            <div className="text-xs text-foreground-500">
              {connected ? (st.verifiedName ? `Nombre verificado: ${st.verifiedName}` : "API de WhatsApp Cloud activa") : st.error ?? "Completa los pasos de abajo para empezar a enviar y recibir mensajes."}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {connected ? <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${q.cls}`}>Calidad del número: {q.label}</span> : null}
          <button className={btnGhost} onClick={() => void load()} disabled={loading}>
            Revisar conexión
          </button>
        </div>
      </div>

      <Step n={1} title="Crea la app en Meta y obtén los datos" done={st.hasToken && !!st.phoneNumberId && !!st.wabaId}>
        <ol className="list-decimal pl-5 text-sm text-foreground-700 space-y-1.5">
          <li>En developers.facebook.com crea una app tipo <b>Empresa</b> y añade el producto <b>WhatsApp</b>.</li>
          <li>En WhatsApp → Configuración de la API anota el <b>ID del número de teléfono</b> y el <b>ID de la cuenta de WhatsApp Business (WABA)</b>.</li>
          <li>En Business Settings → Usuarios del sistema crea uno, asígnale la app y genera un <b>token permanente</b> con los permisos <code>whatsapp_business_messaging</code> y <code>whatsapp_business_management</code>.</li>
          <li>En Configuración de la app → Básica copia el <b>App Secret</b> (sirve para validar que los mensajes vienen de Meta).</li>
        </ol>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <label className="block">
            <span className="text-xs text-foreground-500">Token de acceso permanente {st.hasToken ? "(guardado)" : ""}</span>
            <input type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} placeholder={st.hasToken ? "•••••••• (déjalo vacío para conservarlo)" : "EAAG…"} className={`${inputCls} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs text-foreground-500">App Secret {st.hasAppSecret ? "(guardado)" : "(obligatorio para recibir)"}</span>
            <input type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={st.hasAppSecret ? "•••••••• (déjalo vacío para conservarlo)" : "a1b2c3…"} className={`${inputCls} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs text-foreground-500">ID del número de teléfono</span>
            <input value={phoneId} onChange={(e) => setPhoneId(e.target.value.replace(/\D/g, ""))} placeholder="123456789012345" className={`${inputCls} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs text-foreground-500">ID de la cuenta de WhatsApp Business (WABA)</span>
            <input value={waba} onChange={(e) => setWaba(e.target.value.replace(/\D/g, ""))} placeholder="123456789012345" className={`${inputCls} mt-1`} />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button className={btnPrimary} disabled={busy} onClick={() => void save()}>
            Guardar datos
          </button>
          {st.hasToken || st.phoneNumberId ? (
            <button
              className={btnDanger}
              disabled={busy}
              onClick={async () => {
                if (!window.confirm("¿Borrar las credenciales guardadas? El agente y los envíos dejarán de funcionar hasta que las vuelvas a guardar.")) return;
                setBusy(true);
                await clearWaCredentials();
                setBusy(false);
                notify("Credenciales borradas");
                await load();
              }}
            >
              Borrar credenciales
            </button>
          ) : null}
        </div>
        <p className="text-[11px] text-foreground-500 mt-3">Los datos se guardan cifrados en el servidor y nunca se vuelven a mostrar en pantalla.</p>
      </Step>

      <Step n={2} title="Conecta el webhook para recibir mensajes" done={connected && st.hasAppSecret}>
        <p className="text-sm text-foreground-700 mb-3">
          En tu app de Meta → WhatsApp → Configuración → Webhook pulsa <b>Editar</b>, pega estos dos valores, verifica y activa el campo <b>messages</b>.
        </p>
        <div className="space-y-3">
          <CopyField label="URL de devolución de llamada" value={st.webhookUrl} notify={notify} />
          <CopyField label="Token de verificación" value={st.verifyToken} notify={notify} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            className={btnGhost}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              const r = await subscribeWaApp();
              setBusy(false);
              notify(r.ok ? "App suscrita a tu cuenta de WhatsApp" : r.error ?? "No se pudo suscribir");
            }}
          >
            Suscribir la app a mi cuenta
          </button>
          <button
            className={btnGhost}
            disabled={busy}
            onClick={async () => {
              if (!window.confirm("Se generará un token nuevo y tendrás que actualizarlo en Meta. ¿Continuar?")) return;
              setBusy(true);
              const r = await regenVerifyToken();
              setBusy(false);
              notify(r.ok ? "Token nuevo generado" : "No se pudo generar");
              await load();
            }}
          >
            Generar otro token
          </button>
        </div>
      </Step>

      <Step n={3} title="Crea y aprueba las plantillas" done={false}>
        <p className="text-sm text-foreground-700">
          Para escribirle primero a alguien (confirmación del webinar, recuperar a quien lo dejó a medias) Meta exige una plantilla aprobada. Ya tienes las dos del webinar listas para enviar a revisión.
        </p>
        <button className={`${btnPrimary} mt-3`} onClick={goTemplates}>
          Ir a Plantillas
        </button>
      </Step>

      <Step n={4} title="Envía un mensaje de prueba" done={false}>
        <p className="text-sm text-foreground-700 mb-3">Con la plantilla ya aprobada. En modo de pruebas de Meta solo puedes escribir a números que hayas añadido en la lista de destinatarios.</p>
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
          <input value={testTo} onChange={(e) => setTestTo(e.target.value.replace(/\D/g, ""))} placeholder="Número con país, ej. 573001234567" className={inputCls} />
          <input value={testName} onChange={(e) => setTestName(e.target.value.trim())} placeholder="Nombre de la plantilla" className={inputCls} />
          <button
            className={btnPrimary}
            disabled={busy || !connected || testTo.length < 8}
            onClick={async () => {
              setBusy(true);
              const r = await sendTemplate({ to: testTo, name: testName, language: "es", params: ["Prueba", "https://app.vivelo-ai.com/webinar"] });
              setBusy(false);
              notify(r.ok ? "Mensaje enviado. Revisa tu WhatsApp." : r.error ?? "No se pudo enviar");
            }}
          >
            Enviar prueba
          </button>
        </div>
      </Step>

      <div className={`${cardCls} text-sm text-foreground-700`}>
        <div className="font-heading font-bold text-foreground-950 mb-1">Motor de IA</div>
        {st.ollama ? (
          <span className="text-primary-700 font-semibold">
            <i className="ri-check-line"></i> Activo (Ollama Cloud)
          </span>
        ) : (
          <span className="text-accent-700 font-semibold">Falta el secret OLLAMA_API_KEY en Supabase: sin él el agente no puede redactar respuestas.</span>
        )}
      </div>
    </div>
  );
}
